import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import DraggableMediaList from '../components/DraggableMediaList';
import './PropertyForm.css';

const EditProperty = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingProperty, setFetchingProperty] = useState(true);
  const [existingImages, setExistingImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [existingVideos, setExistingVideos] = useState([]);
  const [selectedVideos, setSelectedVideos] = useState([]);
  const [uploadingVideos, setUploadingVideos] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'apartment',
    property_category: 'single_unit',
    listing_subtype: 'entire_place',
    square_footage: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip_code: '',
    bedrooms: 1,
    bathrooms: 1,
    beds: 1,
    max_guests: 2,
    base_guests: 2,
    price_per_night: '',
    extra_guest_price: 0,
    cleaning_fee: 0,
    service_fee: 0,
    check_in_time: '15:00',
    check_out_time: '11:00',
    minimum_nights: 1,
    maximum_nights: 30,
    cancellation_policy: 'flexible',
    house_rules: '',
    amenity_ids: []
  });

  useEffect(() => {
    fetchAmenities();
    fetchProperty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchAmenities = async () => {
    try {
      const response = await api.get('/api/properties/amenities');
      setAmenities(response.data.amenities);
    } catch (error) {
      console.error('Error fetching amenities:', error);
    }
  };

  const fetchProperty = async () => {
    try {
      setFetchingProperty(true);
      const response = await api.get(`/api/properties/${id}`);
      const property = response.data.property;

      // Verify that the current user is the host of this property
      if (user && property.host_id !== user.id) {
        toast.error('Você não tem permissão para editar esta propriedade');
        navigate('/host/dashboard');
        return;
      }

      // Pre-populate form with existing property data
      setFormData({
        title: property.title || '',
        description: property.description || '',
        property_type: property.property_type || 'apartment',
        property_category: property.property_category || 'single_unit',
        listing_subtype: property.listing_subtype || 'entire_place',
        square_footage: property.square_footage || '',
        address: property.address || '',
        city: property.city || '',
        state: property.state || '',
        country: property.country || '',
        zip_code: property.zip_code || '',
        bedrooms: property.bedrooms || 1,
        bathrooms: property.bathrooms || 1,
        beds: property.beds || 1,
        max_guests: property.max_guests || 2,
        base_guests: property.base_guests || 2,
        price_per_night: property.price_per_night || '',
        extra_guest_price: property.extra_guest_price || 0,
        cleaning_fee: property.cleaning_fee || 0,
        service_fee: property.service_fee || 0,
        check_in_time: property.check_in_time || '15:00',
        check_out_time: property.check_out_time || '11:00',
        minimum_nights: property.minimum_nights || 1,
        maximum_nights: property.maximum_nights || 30,
        cancellation_policy: property.cancellation_policy || 'flexible',
        house_rules: property.house_rules || '',
        amenity_ids: property.amenities ? property.amenities.map(a => a.id) : []
      });

      // Load existing images and videos
      setExistingImages(property.images || []);
      setExistingVideos(property.videos || []);
    } catch (error) {
      console.error('Error fetching property:', error);
      const message = error.response?.data?.error || 'Falha ao carregar propriedade';
      toast.error(message);
      navigate('/host/dashboard');
    } finally {
      setFetchingProperty(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleAmenityToggle = (amenityId) => {
    setFormData(prev => ({
      ...prev,
      amenity_ids: prev.amenity_ids.includes(amenityId)
        ? prev.amenity_ids.filter(id => id !== amenityId)
        : [...prev.amenity_ids, amenityId]
    }));
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedImages(prev => [...prev, ...files]);
  };

  const handleRemoveNewImage = (index) => {
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingImage = async (index) => {
    if (!window.confirm(t('editProperty.confirmRemoveImage'))) {
      return;
    }

    try {
      const imageToRemove = existingImages[index];
      if (imageToRemove) {
        // Delete from server
        await api.delete('/api/uploads/image', {
          data: { image_url: imageToRemove.image_url }
        });
      }

      // Remove from UI
      setExistingImages(prev => prev.filter((_, i) => i !== index));
      toast.success(t('editProperty.imageRemovedSuccess'));
    } catch (error) {
      console.error('Error removing image:', error);
      toast.error('Erro ao remover imagem');
    }
  };

  const handleReorderImages = async (reorderedImages) => {
    try {
      // Update local state immediately for better UX
      setExistingImages(reorderedImages);

      // Send the new order to the backend
      const imageIds = reorderedImages.map(img => img.id);
      await api.put(`/api/properties/${id}/images/reorder`, {
        image_ids: imageIds
      });

      toast.success('Ordem das imagens atualizada!');
    } catch (error) {
      console.error('Error reordering images:', error);
      toast.error('Erro ao reordenar imagens');
      // Revert on error
      await fetchProperty();
    }
  };

  const handleSetPrimaryImage = async (index) => {
    const reorderedImages = [...existingImages];
    const [primaryImage] = reorderedImages.splice(index, 1);
    reorderedImages.unshift(primaryImage);
    await handleReorderImages(reorderedImages);
  };

  const uploadImages = async (propertyId) => {
    if (selectedImages.length === 0) return;

    setUploadingImages(true);
    try {
      for (let i = 0; i < selectedImages.length; i++) {
        const file = selectedImages[i];
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        // Upload the image file
        const uploadResponse = await api.post('/api/uploads/image?folder=properties', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const imageUrl = uploadResponse.data.image_url;

        // Add the image to the property
        await api.post(`/api/properties/${propertyId}/images`, {
          image_url: imageUrl,
          is_primary: existingImages.length === 0 && i === 0 // First image is primary if no existing images
        });
      }

      // Refresh property data to show new images
      await fetchProperty();
      setSelectedImages([]);
    } catch (error) {
      console.error('Error uploading images:', error);
      toast.error('Erro ao enviar algumas imagens');
    } finally {
      setUploadingImages(false);
    }
  };

  const handleVideoSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedVideos(prev => [...prev, ...files]);
  };

  const handleRemoveNewVideo = (index) => {
    setSelectedVideos(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveExistingVideo = async (index) => {
    if (!window.confirm(t('editProperty.confirmRemoveVideo'))) {
      return;
    }

    try {
      const videoToRemove = existingVideos[index];
      if (videoToRemove) {
        // Delete from server
        await api.delete('/api/uploads/video', {
          data: { video_url: videoToRemove.video_url }
        });
      }

      // Remove from UI
      setExistingVideos(prev => prev.filter((_, i) => i !== index));
      toast.success('Vídeo removido com sucesso!');
    } catch (error) {
      console.error('Error removing video:', error);
      toast.error('Erro ao remover vídeo');
    }
  };

  const handleReorderVideos = async (reorderedVideos) => {
    try {
      // Update local state immediately for better UX
      setExistingVideos(reorderedVideos);

      // Send the new order to the backend
      const videoIds = reorderedVideos.map(vid => vid.id);
      await api.put(`/api/properties/${id}/videos/reorder`, {
        video_ids: videoIds
      });

      toast.success('Ordem dos vídeos atualizada!');
    } catch (error) {
      console.error('Error reordering videos:', error);
      toast.error('Erro ao reordenar vídeos');
      // Revert on error
      await fetchProperty();
    }
  };

  const uploadVideos = async (propertyId) => {
    if (selectedVideos.length === 0) return;

    setUploadingVideos(true);
    try {
      for (let i = 0; i < selectedVideos.length; i++) {
        const file = selectedVideos[i];
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        // Upload the video file
        const uploadResponse = await api.post('/api/uploads/video?folder=properties', formDataUpload, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        const videoUrl = uploadResponse.data.video_url;

        // Add the video to the property
        await api.post(`/api/properties/${propertyId}/videos`, {
          video_url: videoUrl
        });
      }

      // Refresh property data to show new videos
      await fetchProperty();
      setSelectedVideos([]);
    } catch (error) {
      console.error('Error uploading videos:', error);
      toast.error('Erro ao enviar alguns vídeos');
    } finally {
      setUploadingVideos(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.put(`/api/properties/${id}`, formData);

      // Upload new images if any were selected
      if (selectedImages.length > 0) {
        await uploadImages(id);
      }

      // Upload new videos if any were selected
      if (selectedVideos.length > 0) {
        await uploadVideos(id);
      }

      toast.success('Propriedade atualizada com sucesso!');
      navigate(`/properties/${id}`);
    } catch (error) {
      const message = error.response?.data?.error || 'Falha ao atualizar propriedade';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (fetchingProperty) {
    return (
      <div className="property-form-page">
        <div className="container">
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <p>Carregando propriedade...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="property-form-page">
      <div className="container">
        <h1>Editar Propriedade</h1>
        <p className="form-subtitle">Atualize as informações da sua propriedade</p>

        <form onSubmit={handleSubmit} className="property-form">
          {/* Basic Information */}
          <div className="form-section">
            <h2>{t('createProperty.basicInfo')}</h2>

            <div className="form-group">
              <label className="form-label">{t('createProperty.propertyTitle')} *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="form-input"
                required
                placeholder={t('createProperty.titlePlaceholder')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('createProperty.description')} *</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-textarea"
                rows="6"
                required
                placeholder={t('createProperty.descriptionPlaceholder')}
              />
            </div>

            <div className="form-group">
              <label className="form-label">{t('createProperty.propertyType')} *</label>
              <select
                name="property_type"
                value={formData.property_type}
                onChange={handleChange}
                className="form-select"
                required
              >
                <option value="apartment">{t('filters.apartment')}</option>
                <option value="house">{t('filters.house')}</option>
                <option value="villa">{t('filters.villa')}</option>
                <option value="condo">{t('filters.condo')}</option>
                <option value="cabin">{t('filters.cabin')}</option>
                <option value="cottage">{t('filters.cottage')}</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Categoria do Imóvel</label>
                <select
                  name="property_category"
                  value={formData.property_category}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="single_unit">Unidade Individual</option>
                  <option value="multi_unit">Multi-unidades (Prédio/Complexo)</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Tipo de Listagem</label>
                <select
                  name="listing_subtype"
                  value={formData.listing_subtype}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="entire_place">Lugar Inteiro</option>
                  <option value="private_room">Quarto Privado</option>
                  <option value="shared_room">Quarto Compartilhado</option>
                </select>
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="form-section">
            <h2>{t('createProperty.location')}</h2>

            <div className="form-group">
              <label className="form-label">{t('createProperty.address')} *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="form-input"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.city')} *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('createProperty.state')}</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.country')} *</label>
                <input
                  type="text"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('createProperty.zipCode')}</label>
                <input
                  type="text"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Property Details */}
          <div className="form-section">
            <h2>{t('createProperty.details')}</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.bedrooms')} *</label>
                <input
                  type="number"
                  name="bedrooms"
                  value={formData.bedrooms}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">{t('createProperty.bathrooms')} *</label>
                <input
                  type="number"
                  name="bathrooms"
                  value={formData.bathrooms}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.beds')} *</label>
                <input
                  type="number"
                  name="beds"
                  value={formData.beds}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Área (m²)</label>
                <input
                  type="number"
                  name="square_footage"
                  value={formData.square_footage}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                  placeholder={t('common.squareFootagePlaceholder')}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.maxGuests')} *</label>
                <input
                  type="number"
                  name="max_guests"
                  value={formData.max_guests}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Hóspedes Inclusos no Preço Base *</label>
                <input
                  type="number"
                  name="base_guests"
                  value={formData.base_guests}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                  max={formData.max_guests}
                  required
                />
                <small className="form-hint">Número de hóspedes já inclusos no preço da diária</small>
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div className="form-section">
            <h2>{t('createProperty.pricing')}</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.pricePerNight')} (R$) *</label>
                <input
                  type="number"
                  name="price_per_night"
                  value={formData.price_per_night}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                  required
                />
                <small className="form-hint">Preço base para até {formData.base_guests} hóspede(s)</small>
              </div>

              <div className="form-group">
                <label className="form-label">Preço por Hóspede Adicional (R$)</label>
                <input
                  type="number"
                  name="extra_guest_price"
                  value={formData.extra_guest_price}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
                <small className="form-hint">Valor cobrado por cada hóspede além dos {formData.base_guests} inclusos</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">{t('createProperty.cleaningFee')} (R$)</label>
                <input
                  type="number"
                  name="cleaning_fee"
                  value={formData.cleaning_fee}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Taxa de Serviço (R$)</label>
                <input
                  type="number"
                  name="service_fee"
                  value={formData.service_fee}
                  onChange={handleChange}
                  className="form-input"
                  min="0"
                  step="0.01"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Noites Mínimas</label>
                <input
                  type="number"
                  name="minimum_nights"
                  value={formData.minimum_nights}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Noites Máximas</label>
                <input
                  type="number"
                  name="maximum_nights"
                  value={formData.maximum_nights}
                  onChange={handleChange}
                  className="form-input"
                  min="1"
                />
              </div>
            </div>
          </div>

          {/* Images */}
          <div className="form-section">
            <h2>Fotos do Imóvel</h2>

            {/* Existing images */}
            {existingImages.length > 0 && (
              <div className="existing-images">
                <p>Fotos atuais ({existingImages.length}) - Arraste para reordenar:</p>
                <DraggableMediaList
                  items={existingImages}
                  onReorder={handleReorderImages}
                  onRemove={handleRemoveExistingImage}
                  onSetPrimary={handleSetPrimaryImage}
                  type="image"
                />
              </div>
            )}

            {/* Add new images */}
            <div className="form-group" style={{ marginTop: existingImages.length > 0 ? '24px' : '0' }}>
              <label className="form-label">Adicionar Novas Fotos</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className="form-input"
              />
              <small className="form-hint">Você pode selecionar múltiplas imagens.</small>
            </div>

            {selectedImages.length > 0 && (
              <div className="selected-images">
                <p><strong>{selectedImages.length}</strong> nova(s) imagem(ns) selecionada(s) - Arraste para reordenar:</p>
                <DraggableMediaList
                  items={selectedImages.map((file, idx) => ({
                    id: `new-${idx}`,
                    preview: URL.createObjectURL(file),
                    file: file
                  }))}
                  onReorder={(reordered) => setSelectedImages(reordered.map(item => item.file))}
                  onRemove={handleRemoveNewImage}
                  type="image"
                />
              </div>
            )}
          </div>

          {/* Videos */}
          <div className="form-section">
            <h2>Vídeos do Imóvel</h2>

            {/* Existing videos */}
            {existingVideos.length > 0 && (
              <div className="existing-videos">
                <p>Vídeos atuais ({existingVideos.length}) - Arraste para reordenar:</p>
                <DraggableMediaList
                  items={existingVideos}
                  onReorder={handleReorderVideos}
                  onRemove={handleRemoveExistingVideo}
                  type="video"
                />
              </div>
            )}

            {/* Add new videos */}
            <div className="form-group" style={{ marginTop: existingVideos.length > 0 ? '24px' : '0' }}>
              <label className="form-label">Adicionar Novos Vídeos</label>
              <input
                type="file"
                accept="video/*"
                multiple
                onChange={handleVideoSelect}
                className="form-input"
              />
              <small className="form-hint">Você pode selecionar múltiplos vídeos. Formatos: MP4, MOV, AVI, WEBM, MKV.</small>
            </div>

            {selectedVideos.length > 0 && (
              <div className="selected-videos">
                <p><strong>{selectedVideos.length}</strong> novo(s) vídeo(s) selecionado(s) - Arraste para reordenar:</p>
                <DraggableMediaList
                  items={selectedVideos.map((file, idx) => ({
                    id: `new-${idx}`,
                    preview: URL.createObjectURL(file),
                    file: file
                  }))}
                  onReorder={(reordered) => setSelectedVideos(reordered.map(item => item.file))}
                  onRemove={handleRemoveNewVideo}
                  type="video"
                />
              </div>
            )}
          </div>

          {/* Amenities */}
          <div className="form-section">
            <h2>{t('createProperty.amenities')}</h2>
            <div className="amenities-grid">
              {amenities.map(amenity => (
                <label key={amenity.id} className="amenity-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.amenity_ids.includes(amenity.id)}
                    onChange={() => handleAmenityToggle(amenity.id)}
                  />
                  <span>{amenity.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* House Rules */}
          <div className="form-section">
            <h2>{t('propertyDetail.houseRules')}</h2>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Horário de Check-in</label>
                <input
                  type="time"
                  name="check_in_time"
                  value={formData.check_in_time}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Horário de Check-out</label>
                <input
                  type="time"
                  name="check_out_time"
                  value={formData.check_out_time}
                  onChange={handleChange}
                  className="form-input"
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Política de Cancelamento</label>
              <select
                name="cancellation_policy"
                value={formData.cancellation_policy}
                onChange={handleChange}
                className="form-select"
              >
                <option value="flexible">Flexível</option>
                <option value="moderate">Moderada</option>
                <option value="strict">Rigorosa</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{t('propertyDetail.houseRules')}</label>
              <textarea
                name="house_rules"
                value={formData.house_rules}
                onChange={handleChange}
                className="form-textarea"
                rows="4"
                placeholder={t('createProperty.houseRulesPlaceholder')}
              />
            </div>
          </div>

          {/* Submit */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(`/properties/${id}`)}
              className="btn btn-secondary btn-lg"
              disabled={loading || uploadingImages || uploadingVideos}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-lg"
              disabled={loading || uploadingImages || uploadingVideos}
            >
              {uploadingVideos ? 'Enviando vídeos...' : uploadingImages ? 'Enviando fotos...' : loading ? 'Salvando...' : 'Salvar Alterações'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProperty;
